import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash, Camera } from '@phosphor-icons/react';
import { formatCurrency } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewLoan = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [marketRates, setMarketRates] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    ltv_percentage: 75,
    interest_rate: 12,
    tenure_months: 12,
    interest_type: 'simple'
  });
  
  const [items, setItems] = useState([{
    metal_type: 'gold',
    gross_weight: '',
    net_weight: '',
    purity: 22,
    description: '',
    photos: [],
    storage_location: ''
  }]);

  const [selectedMetalRate, setSelectedMetalRate] = useState(0);

  useEffect(() => {
    fetchCustomers();
    fetchMarketRates();
  }, []);

  const fetchCustomers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/customers`, {
        withCredentials: true
      });
      setCustomers(data);
    } catch (error) {
      toast.error('Failed to fetch customers');
    }
  };

  const fetchMarketRates = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/market-rates`, {
        withCredentials: true
      });
      setMarketRates(data);
    } catch (error) {
      console.error('Failed to fetch market rates:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handlePhotoUpload = (index, e) => {
    const files = Array.from(e.target.files);
    const readers = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
    });

    Promise.all(readers).then(results => {
      const newItems = [...items];
      newItems[index].photos = [...newItems[index].photos, ...results];
      setItems(newItems);
    });
  };

  const addItem = () => {
    setItems([...items, {
      metal_type: 'gold',
      gross_weight: '',
      net_weight: '',
      purity: 22,
      description: '',
      photos: [],
      storage_location: ''
    }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const calculateTotalValue = () => {
    let total = 0;
    items.forEach(item => {
      if (item.net_weight && selectedMetalRate) {
        total += parseFloat(item.net_weight) * selectedMetalRate;
      }
    });
    return total;
  };

  const calculateLoanAmount = () => {
    const totalValue = calculateTotalValue();
    return totalValue * (formData.ltv_percentage / 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const loanData = {
        ...formData,
        items: items,
        market_rate: selectedMetalRate,
        ltv_percentage: parseFloat(formData.ltv_percentage),
        interest_rate: parseFloat(formData.interest_rate),
        tenure_months: parseInt(formData.tenure_months)
      };

      await axios.post(`${API_URL}/api/loans`, loanData, {
        withCredentials: true
      });

      toast.success('Loan created successfully');
      navigate('/vault');
    } catch (error) {
      toast.error('Failed to create loan');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentRate = (metalType) => {
    const rate = marketRates.find(r => r.metal_type === metalType);
    return rate ? rate.rate_per_gram : 0;
  };

  return (
    <div data-testid="new-loan-page">
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
          Create New Loan
        </h1>
        <p className="text-[#6B7280] mt-2">Process a new gold or silver loan application</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Customer Selection */}
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
          <h3 className="text-lg font-semibold text-[#1A1A1A] mb-4" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Customer Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="customer_id">Select Customer *</Label>
              <Select
                value={formData.customer_id}
                onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                required
              >
                <SelectTrigger data-testid="customer-select">
                  <SelectValue placeholder="Choose a customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.length > 0 && customers?.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name} - {customer.phone}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#1A1A1A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Gold/Silver Items
            </h3>
            <Button type="button" onClick={addItem} variant="outline" size="sm" data-testid="add-item-button">
              <Plus size={16} className="mr-2" />
              Add Item
            </Button>
          </div>

          {items?.map((item, index) => (
            <div key={index} className="mb-6 pb-6 border-b border-[#E5E7EB] last:border-0">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-[#1A1A1A]">Item {index + 1}</h4>
                {items?.length > 1 && (
                  <Button 
                    type="button" 
                    onClick={() => removeItem(index)} 
                    variant="ghost" 
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    data-testid={`remove-item-${index}`}
                  >
                    <Trash size={16} />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Metal Type *</Label>
                  <Select
                    value={item.metal_type}
                    onValueChange={(value) => {
                      handleItemChange(index, 'metal_type', value);
                      setSelectedMetalRate(getCurrentRate(value));
                    }}
                  >
                    <SelectTrigger data-testid={`metal-type-${index}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gold">Gold</SelectItem>
                      <SelectItem value="silver">Silver</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Gross Weight (grams) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={item.gross_weight}
                    onChange={(e) => handleItemChange(index, 'gross_weight', e.target.value)}
                    required
                    data-testid={`gross-weight-${index}`}
                  />
                </div>

                <div>
                  <Label>Net Weight (grams) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={item.net_weight}
                    onChange={(e) => handleItemChange(index, 'net_weight', e.target.value)}
                    required
                    data-testid={`net-weight-${index}`}
                  />
                </div>

                <div>
                  <Label>Purity (Karat/%) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={item.purity}
                    onChange={(e) => handleItemChange(index, 'purity', e.target.value)}
                    required
                    data-testid={`purity-${index}`}
                  />
                </div>

                <div>
                  <Label>Description *</Label>
                  <Input
                    value={item.description}
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                    placeholder="e.g., Gold necklace, Silver bracelet"
                    required
                    data-testid={`description-${index}`}
                  />
                </div>

                <div>
                  <Label>Storage Location</Label>
                  <Input
                    value={item.storage_location}
                    onChange={(e) => handleItemChange(index, 'storage_location', e.target.value)}
                    placeholder="e.g., Locker A1"
                    data-testid={`storage-${index}`}
                  />
                </div>
              </div>

              <div className="mt-4">
                <Label>Item Photos</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById(`photo-${index}`).click()}>
                    <Camera size={16} className="mr-2" />
                    Upload Photos
                  </Button>
                  <input
                    id={`photo-${index}`}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handlePhotoUpload(index, e)}
                  />
                  <span className="text-sm text-[#6B7280]">{item.photos.length} photo(s) uploaded</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Loan Terms */}
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
          <h3 className="text-lg font-semibold text-[#1A1A1A] mb-4" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Loan Terms
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="market_rate">Market Rate (per gram) *</Label>
              <Input
                id="market_rate"
                type="number"
                step="0.01"
                value={selectedMetalRate}
                onChange={(e) => setSelectedMetalRate(parseFloat(e.target.value))}
                required
                data-testid="market-rate-input"
              />
              <p className="text-xs text-[#6B7280] mt-1">
                Current {items[0]?.metal_type} rate: ₹{getCurrentRate(items[0]?.metal_type)}/gram
              </p>
            </div>

            <div>
              <Label htmlFor="ltv_percentage">LTV Percentage *</Label>
              <Input
                id="ltv_percentage"
                name="ltv_percentage"
                type="number"
                step="0.01"
                value={formData.ltv_percentage}
                onChange={handleChange}
                required
                data-testid="ltv-input"
              />
            </div>

            <div>
              <Label htmlFor="interest_rate">Interest Rate (% per annum) *</Label>
              <Input
                id="interest_rate"
                name="interest_rate"
                type="number"
                step="0.01"
                value={formData.interest_rate}
                onChange={handleChange}
                required
                data-testid="interest-rate-input"
              />
            </div>

            <div>
              <Label htmlFor="tenure_months">Tenure (months) *</Label>
              <Input
                id="tenure_months"
                name="tenure_months"
                type="number"
                value={formData.tenure_months}
                onChange={handleChange}
                required
                data-testid="tenure-input"
              />
            </div>

            <div>
              <Label htmlFor="interest_type">Interest Type</Label>
              <Select
                value={formData.interest_type}
                onValueChange={(value) => setFormData({ ...formData, interest_type: value })}
              >
                <SelectTrigger data-testid="interest-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="simple">Simple Interest</SelectItem>
                  <SelectItem value="compound">Compound Interest</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Loan Summary */}
        <div className="bg-[#C9A227]/10 rounded-lg border border-[#C9A227]/30 p-6">
          <h3 className="text-lg font-semibold text-[#1A1A1A] mb-4" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Loan Summary
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-[#6B7280]">Total Appraised Value:</span>
              <span className="text-2xl font-bold text-[#1A1A1A] font-mono-num" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {formatCurrency(calculateTotalValue())}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[#6B7280]">Loan Amount ({formData.ltv_percentage}% LTV):</span>
              <span className="text-3xl font-bold text-[#C9A227] font-mono-num" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="calculated-loan-amount">
                {formatCurrency(calculateLoanAmount())}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pb-16">
          <Button type="button" variant="outline" onClick={() => navigate('/vault')}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={loading || !formData.customer_id || items.length === 0}
            className="bg-[#C9A227] hover:bg-[#A9851F]"
            data-testid="create-loan-button"
          >
            {loading ? 'Creating...' : 'Create Loan'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default NewLoan;
